import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../../api/httpClient";
import InfoCard from "../../components/card/infocard";
import StaffListModal from "../../components/company-settings/StaffListModal";
import {
  DeleteItem,
  InviteModal,
  SuccessfulDelete,
  SuccessModal,
} from "../../components/modal";
import DeleteImg from "../../assets/delLogo.png";
import verifyImg from "../../assets/verifyImg.png";
import { SimpleButton } from "../../components/button.jsx/Btn";
import { getApiBaseUrl } from "../../data/authStorage";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import PageIntroNav from "../../components/navigation/PageIntroNav";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";

const PositionViewDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const apiBaseUrl = getApiBaseUrl();
  const positionId = useMemo(
    () => location.state?.positionId || sessionStorage.getItem("selectedPositionId"),
    [location.state],
  );
  const cacheKey = useMemo(() => `cehr_position_details_${positionId || "unknown"}`, [positionId]);
  const cachedData = useMemo(() => readSessionCache(cacheKey, { position: null }), [cacheKey]);

  const [position, setPosition] = useState(cachedData?.position || null);
  const [isLoading, setIsLoading] = useState(!cachedData?.position);
  const [errorMessage, setErrorMessage] = useState("");
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openDeleteSuccessModal, setOpenDeleteSuccessModal] = useState(false);
  const [positionName, setPositionName] = useState("");
  const [positionDescription, setPositionDescription] = useState("");
  const [formErrorMessage, setFormErrorMessage] = useState("");
  const [deleteErrorMessage, setDeleteErrorMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isStaffModalOpen, setIsStaffModalOpen] = useState(false);
  const [staffMembers, setStaffMembers] = useState([]);
  const [isStaffLoading, setIsStaffLoading] = useState(false);
  const [staffErrorMessage, setStaffErrorMessage] = useState("");

  const resolveStaffList = (response) => {
    const payload = response?.data || {};

    if (Array.isArray(payload.staff)) return payload.staff;
    if (Array.isArray(payload.employees)) return payload.employees;
    if (Array.isArray(payload.data)) return payload.data;

    return [];
  };

  const loadPositionStaff = useCallback(async () => {
    if (!apiBaseUrl || !positionId) {
      setStaffErrorMessage("Unable to load staff for this position right now.");
      return;
    }

    try {
      setIsStaffLoading(true);
      setStaffErrorMessage("");

      const response = await axios.get(`${apiBaseUrl}/organizations/positions/${positionId}/staff`);
      setStaffMembers(resolveStaffList(response));
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message || "Unable to load staff for this position. Please try again.";
      setStaffErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
      setStaffMembers([]);
    } finally {
      setIsStaffLoading(false);
    }
  }, [apiBaseUrl, positionId]);

  const openStaffModal = () => {
    setIsStaffModalOpen(true);
    void loadPositionStaff();
  };

  const loadPosition = useCallback(async ({ silent = false } = {}) => {
      if (!apiBaseUrl || !positionId) {
        setErrorMessage(!positionId ? "Select a position from the list first." : "API base URL is not configured.");
        return;
      }

      try {
        if (!silent) {
          setIsLoading(true);
        }
        setErrorMessage("");

        const response = await axios.get(`${apiBaseUrl}/organizations/positions/${positionId}`);
        const loadedPosition = response?.data?.position || null;

        setPosition(loadedPosition);
        setPositionName(loadedPosition?.positionName || "");
        setPositionDescription(loadedPosition?.positionDescription || "");
        writeSessionCache(cacheKey, { position: loadedPosition });
      } catch (error) {
        const responseMessage =
          error?.response?.data?.message || "Unable to load position details. Please try again.";
        setErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
      } finally {
        setIsLoading(false);
      }
    }, [apiBaseUrl, cacheKey, positionId]);

  useEffect(() => {
    loadPosition({ silent: true });
  }, [loadPosition]);

  useEffect(() => {
    const handleFocusRefresh = () => {
      if (document.visibilityState !== "hidden") {
        loadPosition({ silent: true });
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, [loadPosition]);

  const refreshPosition = async () => {
    if (!apiBaseUrl || !positionId) {
      return;
    }

    const response = await axios.get(`${apiBaseUrl}/organizations/positions/${positionId}`);
    const loadedPosition = response?.data?.position || null;

    setPosition(loadedPosition);
    setPositionName(loadedPosition?.positionName || "");
    setPositionDescription(loadedPosition?.positionDescription || "");
  };

  const handleOpenEditModal = () => {
    setFormErrorMessage("");
    setOpenEditModal(true);
  };

  const openDeletePosition = () => {
    setDeleteErrorMessage("");
    setOpenDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setOpenDeleteModal(false);
    setDeleteErrorMessage("");
  };

  const handleUpdatePosition = async (ev) => {
    ev.preventDefault();

    if (!positionId || !apiBaseUrl) {
      setFormErrorMessage("Unable to update this position right now.");
      return;
    }

    if (!positionName.trim() || !positionDescription.trim()) {
      setFormErrorMessage("Position name and position description are required.");
      return;
    }

    try {
      setIsSaving(true);
      setFormErrorMessage("");

      await axios.patch(`${apiBaseUrl}/organizations/positions/${positionId}`, {
        positionName: positionName.trim(),
        positionDescription: positionDescription.trim(),
      });

      await refreshPosition();
      setOpenEditModal(false);
      setOpenSuccessModal(true);
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message || "Unable to update position. Please try again.";
      setFormErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePosition = async () => {
    if (!positionId || !apiBaseUrl) {
      setDeleteErrorMessage("Unable to delete this position right now.");
      return;
    }

    try {
      setIsDeleting(true);
      setDeleteErrorMessage("");

      await axios.delete(`${apiBaseUrl}/organizations/positions/${positionId}`);

      setOpenDeleteModal(false);
      setOpenDeleteSuccessModal(true);
      sessionStorage.removeItem("selectedPositionId");

      setTimeout(() => {
        navigate("/company-settings/position");
      }, 1200);
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message || "Unable to delete position. Please try again.";
      setDeleteErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const title = position?.positionName || "Position";
  const descriptionText =
    position?.positionDescription || "No position description available yet.";
  const createdAt = position?.createdAt
    ? new Date(position.createdAt).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "-";

  return (
    <div>
      <PageIntroNav
        breadcrumbs={[
          { label: "Company Settings", path: "/company-settings" },
          { label: "Positions", path: "/company-settings/position" },
          { label: title },
        ]}
        title={title}
        subtitle="Position details and management actions."
        fallbackPath="/company-settings/position"
        backLabel="Back to Positions"
        actions={(
          <div className="flex items-center gap-3">
            <button
              onClick={handleOpenEditModal}
              className="border border-[#1234CD] text-[#1234CD] px-4 py-2 rounded-xl"
            >
              Edit
            </button>
            <button
              onClick={openDeletePosition}
              className="border border-red-500 text-red-600 px-4 py-2 rounded-xl"
            >
              Delete
            </button>
          </div>
        )}
      />

      {isLoading && !position ? (
        <FetchLoadingState message="Loading position details..." className="min-h-[60vh]" />
      ) : null}

      {errorMessage && !position ? (
        <div className="px-6 py-4 text-sm text-red-600" role="alert">
          {errorMessage}
        </div>
      ) : null}

      {!isLoading && position ? (
        <InfoCard
          details="Position Details"
          title={title}
          description={descriptionText}
          metaData={[
            { label: "Date created", value: createdAt },
            { label: "Total staff", value: position?.totalStaff ?? 0 },
            { label: "Position ID", value: position?.id || "-" },
          ]}
        />
      ) : null}

      {!isLoading && position ? (
        <div className="px-6 mt-6 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={openStaffModal}
            className="px-4 py-2 rounded-xl bg-[#1234CD] text-white font-medium"
          >
            View Position Staff
          </button>
        </div>
      ) : null}

      <DeleteItem
        isOpen={openDeleteModal}
        onClose={closeDeleteModal}
      >
        <div>
          <img
            className="w-40 mx-auto animate-pulse"
            src={DeleteImg}
            alt="delete position"
          />

          <h1 className="text-lg text-center text-[#000000] font-bold">
            Continue to delete this position?
          </h1>
          <p className="text-sm text-center mt-2">Action cannot be undone.</p>
          {deleteErrorMessage && (
            <p className="text-sm text-center mt-3 text-red-600" role="alert">
              {deleteErrorMessage}
            </p>
          )}
        </div>
        <div className="flex items-center py-4 gap-4 my-4">
          <button
            onClick={closeDeleteModal}
            className="border-[#1233CC] py-1 border-2 text-[#1233CC] font-medium px-4 rounded-[10px] w-[102px]"
          >
            No
          </button>
          <button
            className="text-[16px] font-medium px-4 py-1 bg-[#1233CC] rounded-[10px] w-[102px] text-white disabled:opacity-60 disabled:cursor-not-allowed"
            onClick={handleDeletePosition}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Yes"}
          </button>
        </div>
      </DeleteItem>

      <SuccessfulDelete
        isOpen={openDeleteSuccessModal}
        onClose={() => setOpenDeleteSuccessModal(false)}
      >
        <div>
          <img
            src={verifyImg}
            alt="position deleted"
            className="w-20 animate-ping mx-auto"
          />
          <div className="text-center mt-6">
            <h2 className="text-black text-[20px] leading-[27px] font-bold mb-2">
              Position deleted
            </h2>
            <p className="text-[#74777B] text-[14px] leading-[18.9px]">
              The position was removed successfully.
            </p>
          </div>
        </div>
      </SuccessfulDelete>

      <InviteModal
        isOpen={openEditModal}
        onClose={() => setOpenEditModal(false)}
      >
        <div className="my-8">
          <div>
            <p className="text-2xl font-semibold text-black font-Outfit">Edit Position</p>
          </div>
          <form
            className="my-12"
            onSubmit={handleUpdatePosition}
          >
            {formErrorMessage && (
              <p className="mb-4 text-sm text-red-600" role="alert">
                {formErrorMessage}
              </p>
            )}

            <div>
              <label
                htmlFor="position-name"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-satosh"
              >
                Position Name
              </label>
              <input
                type="text"
                id="position-name"
                value={positionName}
                onChange={(ev) => setPositionName(ev.target.value)}
                className="w-full p-2 text-center mb-2 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div>
              <label
                htmlFor="position-description"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-satosh"
              >
                Position Description
              </label>
              <textarea
                id="position-description"
                value={positionDescription}
                onChange={(ev) => setPositionDescription(ev.target.value)}
                rows={4}
                wrap="soft"
                className="w-full p-2 min-h-36 mb-4 resize-y whitespace-pre-wrap break-words rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div className="flex justify-center gap-12 mt-20 items-end">
              <SimpleButton
                onClick={() => setOpenEditModal(false)}
                type="button"
                className="font-[700] font-satoshi leading-[21.6px] flex justify-center items-center border w-40 rounded-xl py-[16px] border-[#1234CD] text-[#1234CD] text-[16px]"
              >
                Cancel
              </SimpleButton>

              <SimpleButton
                type="submit"
                className="font-[700] font-satoshi leading-[21.6px] flex justify-center items-center w-40 rounded-xl text-white py-[16px] bg-[#1234CD] text-[16px] disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={isSaving}
              >
                <span>{isSaving ? "Updating..." : "Update"}</span>
              </SimpleButton>
            </div>
          </form>
        </div>
      </InviteModal>

      <SuccessModal
        isOpen={openSuccessModal}
        onClose={() => setOpenSuccessModal(false)}
      />

      <StaffListModal
        isOpen={isStaffModalOpen}
        onClose={() => setIsStaffModalOpen(false)}
        title={`${title} Staff`}
        subtitle="Staff assigned to this position."
        staff={staffMembers}
        loading={isStaffLoading}
        error={staffErrorMessage}
        onRefresh={loadPositionStaff}
      />
    </div>
  );
};

export default PositionViewDetails;
